import { connect } from 'react-redux';
import { go, goBack, push } from 'connected-react-router';
import { parsePhoneNumber } from 'libphonenumber-js';
import currencyFormatter from 'currency-formatter';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import OnboardingConfirmationPage from './component';
import { ordinalFormat } from '@common/utils/stringUtils';
import { LANDLORD_STATUS } from '@rental-manager/constants/OnboardingConstants';
import { DEFAULT_ERROR, TAX_ID_ERROR } from '@rental-manager/constants/NotificationConstants';
import { logError } from '@zg-rentals/log-error';

import UserActions from '@common/actions/UserActions';
import AppConstants from '@common/constants/AppConstants';
import {
  editAccountInfoPage,
  editDueDatePage,
  editInviteTenantPage,
  editMoveInCostsPage,
  editPaymentTermPage,
  editRentAmountPage,
  editTaxVerificationPage,
} from '@rental-manager/actions/OnboardingActions';
import {
  createMoveInCosts,
  getFeaturePermissions,
  requestPhoneVerificationCheck,
  sendNotification,
  updateTenantInfo,
  verifyTaxInformation,
} from '@rental-manager/actions/requests';
import { cleanPhoneNumber } from '@common/utils/numbersUtils';
import { closeNotification, showModalNotification } from '@common/actions/NotificationActions';
import { trackValidationError } from '@common/actions/TrackingActions';
import { checkRequestActionSuccess } from '@common/paymentsUtils/api';
import { getAccountLast4 } from '@common/paymentsUtils/accountInfo';
import paths from '@rental-manager/pathsManifest';
import { generateMoveInCostPayload, getMoveInCost } from '@common/paymentsUtils/moveInCostsUtil';

const getAccountInfoDetails = ({ landlordStatus, accountInfo, accountInfoFromBackend }) => {
  const accountLast4 = getAccountLast4({ landlordStatus, accountInfo, accountInfoFromBackend });
  return accountLast4;
};

const getBaseMonthlyAmount = ({ monthlyRent }) => [currencyFormatter.format(monthlyRent / 100, { code: 'USD' })];

const getMonthlyPaymentDate = ({ paymentDayOfTheMonth }) => {
  if (paymentDayOfTheMonth === 31) {
    return ['Last day of the month'];
  }
  return [`${ordinalFormat(paymentDayOfTheMonth)} of the month`];
};

const getPaymentTerm = ({ firstDueDate, monthToMonth, paymentEndDate }) => [
  `First full rent payment: ${formatDate(firstDueDate, 'MMMM d, yyyy')}`,
  monthToMonth ? 'No end date/Month-to-month' : `Payment period ends: ${formatDate(paymentEndDate, 'MMMM d, yyyy')}`,
];

const getTenant = ({ tenantFirstName, tenantLastName, emailAddress }) => [
  `${tenantFirstName} ${tenantLastName}`,
  emailAddress,
];

const mapStateToProps = (state, { propertyId, listing, landlordStatus }) => {
  const {
    acceptTerms,
    paymentSettings,
    accountInfo,
    identityVerificationInfo,
    tenantInvitationInfo,
    moveInCostsSettings,
    identityCheckResponse,
  } = state.payments.landlord.onboarding[propertyId];
  const {
    accountInfo: accountInfoFromBackend,
    currentTerm,
    nextTerm,
    selectedLease,
    listingDetails,
  } = state.payments.landlord.property;
  const { paymentsEncodedLeaseId } = selectedLease || {};
  const rentTermsKey = nextTerm?.rentTermsKey || currentTerm.rentTermsKey;
  const baseMonthlyAmount = getBaseMonthlyAmount(paymentSettings);
  const monthlyPaymentDate = getMonthlyPaymentDate(paymentSettings);
  const paymentTerm = getPaymentTerm(paymentSettings);
  const moveInCosts = getMoveInCost(moveInCostsSettings);
  const depositAccount = [getAccountInfoDetails({ landlordStatus, accountInfo, accountInfoFromBackend })];
  const primaryTenant = getTenant(tenantInvitationInfo);
  const listingName = listing.name;
  const hasLandlordOnboarded =
    landlordStatus !== LANDLORD_STATUS.NOT_STARTED && landlordStatus !== LANDLORD_STATUS.EXTERNAL_ACCOUNT_SET;

  return {
    baseMonthlyAmount,
    monthlyPaymentDate,
    paymentTerm,
    moveInCosts,
    depositAccount,
    primaryTenant,
    listingName,
    hasLandlordOnboarded,
    onboardingInfo: {
      acceptTerms,
      paymentSettings,
      accountInfo,
      identityVerificationInfo,
      tenantInvitationInfo,
      moveInCostsSettings,
      rentTermsKey,
      identityCheckResponse,
      paymentsEncodedLeaseId,
    },
    notification: state.payments.landlord.notification,
    identityVerificationInfo,
    user: state.user,
    currentProperty: state.payments.landlord.property.listingDetails,
    listingDetailsContactPhone: listingDetails.contactPhone,
  };
};

const mapDispatchToProps = (dispatch, { baseUrl, propertyId, landlordStatus, location }: Record<string, any>) => ({
  onSubmit: ({
    identityVerificationInfo,
    moveInCostsSettings,
    rentTermsKey,
    tenantInvitationInfo,
    identityCheckResponse,
  }) =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => {
        const needToSubmitId =
          landlordStatus === LANDLORD_STATUS.NOT_STARTED ||
          landlordStatus === LANDLORD_STATUS.ONBOARDING_STARTED ||
          landlordStatus === LANDLORD_STATUS.EXTERNAL_ACCOUNT_SET;
        if (needToSubmitId) {
          let formattedPhoneNumber;

          try {
            // Parse saved phone number into the format expected by the backend: eg. "+11234567890"
            formattedPhoneNumber = parsePhoneNumber(identityVerificationInfo.phoneNumber, 'US').number;
          } catch (err) {
            logError({
              error: new Error(
                `Unable to parse phone number: ${identityVerificationInfo.phoneNumber} for property ${propertyId}`,
              ),
            });
          }

          const businessStructure =
            identityVerificationInfo.selectedTaxVerificationType === 'single_member_llc' ||
            identityVerificationInfo.selectedTaxVerificationType === 'multi_member_llc'
              ? identityVerificationInfo.selectedTaxVerificationType
              : '';

          const payload = {
            identityVerificationInfo: {
              firstName: identityVerificationInfo.firstName,
              lastName: identityVerificationInfo.lastName,
              address: identityVerificationInfo.street,
              city: identityVerificationInfo.city,
              state: identityVerificationInfo.state,
              zip: identityVerificationInfo.zipCode,
              dobDay: identityVerificationInfo.dayOfBirth,
              dobMonth: identityVerificationInfo.monthOfBirth,
              dobYear: identityVerificationInfo.yearOfBirth,
              ssn4: identityVerificationInfo.lastFourDigitsOfSSN,
              phoneNumber: formattedPhoneNumber,
              ssn: identityVerificationInfo.fullSocialSecurityNumber,
              companyAddress: identityVerificationInfo.businessStreet,
              companyCity: identityVerificationInfo.businessCity,
              companyState: identityVerificationInfo.businessState,
              companyZip: identityVerificationInfo.businessZipCode,
              businessType:
                identityVerificationInfo.selectedTaxVerificationType === 'individual' ? 'individual' : 'company',
              companyStructure: businessStructure,
              companyName: identityVerificationInfo.businessName,
              companyDbaName: identityVerificationInfo.dbaName,
              companyTaxId: identityVerificationInfo.employerIdentificationNumber,
            },
          };
          return dispatch(verifyTaxInformation({ propertyId, ...payload }));
        }
        return { success: true };
      })
      .then((verifyIdentityResponse) => {
        if (verifyIdentityResponse?.payload?.data?.verifyErrors?.taxId === 'failed') {
          const error = { success: false, ...verifyIdentityResponse?.payload?.data?.verifyErrors };
          throw error;
        }
        return { success: true };
      })
      .then(() => {
        const { securityDepositSettings, proratedRentSettings, otherSettings, dueDate } = moveInCostsSettings;
        const moveInCosts = generateMoveInCostPayload(moveInCostsSettings);

        const payload = {
          dueDate,
          moveInCosts,
          rentTermsKey,
          isOnboarding: true,
          identityCheckResponse,
        };

        if (
          proratedRentSettings.proratedRentStartsOpen === true ||
          securityDepositSettings.securityDepositStartsOpen === true ||
          otherSettings.length !== 0
        ) {
          return dispatch(createMoveInCosts({ propertyId, ...payload }));
        }
        return { success: true };
      })
      .then(checkRequestActionSuccess)
      .then(() => {
        const payload = {
          propertyId,
          rentTermsKey,
          tenantInfo: {
            email: tenantInvitationInfo.emailAddress,
            firstName: tenantInvitationInfo.tenantFirstName,
            lastName: tenantInvitationInfo.tenantLastName,
          },
        };
        return dispatch(updateTenantInfo({ ...payload, propertyId }));
      })
      .then(checkRequestActionSuccess)
      .then(() => {
        return { success: true };
      })
      .then(checkRequestActionSuccess)
      .then(() => {
        dispatch(
          sendNotification({
            propertyId,
            notificationType: 'rachelPaymentInvite',
          }),
        );
        try {
          return dispatch(
            UserActions.getUser({
              appName: AppConstants.RENTAL_MANAGER,
              pageWithSensitiveInformation: true,
              includeSummary: false,
              brand: 'zillow',
            }),
          );
        } catch (error) {
          logError({
            error: new Error(`Failed to fetch user`),
          });
        }
      })
      .then(() => dispatch(push(baseUrl + paths.onboarding.postConfirmationPage)))
      .catch((err) => {
        if (err?.taxId === 'failed') {
          dispatch(showModalNotification(TAX_ID_ERROR));
          dispatch(editTaxVerificationPage({ propertyId }));
          return dispatch(push(baseUrl + paths.onboarding.taxVerificationPage, { fromWhere: 'onboardingReview' }));
        }
        logError({ error: err });
        dispatch(showModalNotification(DEFAULT_ERROR));
        return { error: true };
      }),
  showModalError: () => dispatch(showModalNotification(DEFAULT_ERROR)),
  editMoveInCosts: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(editMoveInCostsPage({ propertyId })))
      .then(() => dispatch(getFeaturePermissions({ propertyId })))
      .then(() => dispatch(push(baseUrl + paths.onboarding.moveInCostsPage, { fromWhere: 'onboardingReview' }))),
  editRentAmount: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(editRentAmountPage({ propertyId })))
      .then(() => dispatch(push(baseUrl + paths.onboarding.rentAmountPage, { fromWhere: 'onboardingReview' }))),
  editDueDate: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(editDueDatePage({ propertyId })))
      .then(() => dispatch(push(baseUrl + paths.onboarding.dueDatePage, { fromWhere: 'onboardingReview' }))),
  editPaymentTerm: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(editPaymentTermPage({ propertyId })))
      .then(() => dispatch(push(baseUrl + paths.onboarding.termLengthPage, { fromWhere: 'onboardingReview' }))),

  editAccountInfo: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(editAccountInfoPage({ propertyId })))
      .then(() => {
        return dispatch(push(baseUrl + paths.onboarding.accountInfoPage, { fromWhere: 'onboardingReview' }));
      }),
  goToPhoneVerificationPage: () => {
    dispatch(closeNotification());
    dispatch(push(baseUrl + paths.onboarding.phoneVerificationPage, { fromWhere: paths.onboarding.confirmationPage }));
  },
  editIdentityVerification: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(editTaxVerificationPage({ propertyId })))
      .then(() => dispatch(push(baseUrl + paths.onboarding.taxVerificationPage, { fromWhere: 'onboardingReview' }))),
  editTenantInvitationInfo: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(editInviteTenantPage({ propertyId })))
      .then(() => dispatch(push(baseUrl + paths.onboarding.inviteTenantPage, { fromWhere: 'onboardingReview' }))),
  onBack: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => {
        const fromPhoneVerification = location?.state?.fromWhere === paths.onboarding.phoneVerificationPage;
        return dispatch(fromPhoneVerification ? go(-2) : goBack());
      }),
  onClose: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(push(baseUrl + paths.onboarding.exitPaymentSetup))),
  trackValidationError: (data) => dispatch(trackValidationError(data)),
  checkIsPhoneVerificationNeeded: async ({
    phoneNumberFromOnboarding,
    listingDetailsContactPhone,
  }): Promise<boolean> => {
    const action = await dispatch(
      requestPhoneVerificationCheck({
        propertyId,
        phoneNumber: cleanPhoneNumber(phoneNumberFromOnboarding || listingDetailsContactPhone),
      }),
    );
    await checkRequestActionSuccess(action);
    if (
      !action.payload ||
      !action.payload.response ||
      !action.payload.response.isSessionValid ||
      !action.payload.response.isVerified
    ) {
      return true;
    }
    return false;
  },
});

const mergeProps = (stateProps, dispatchProps) => {
  const { identityVerificationInfo, moveInCostsSettings, rentTermsKey, tenantInvitationInfo, identityCheckResponse } =
    stateProps.onboardingInfo;
  return {
    ...stateProps,
    ...dispatchProps,
    onSubmit: () =>
      dispatchProps.onSubmit({
        identityVerificationInfo,
        moveInCostsSettings,
        rentTermsKey,
        tenantInvitationInfo,
        identityCheckResponse,
      }),
    checkIsPhoneVerificationNeeded: () =>
      dispatchProps.checkIsPhoneVerificationNeeded({
        phoneNumberFromOnboarding: stateProps.identityVerificationInfo.phoneNumber,
        listingDetailsContactPhone: stateProps.listingDetailsContactPhone,
      }),
  };
};

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(OnboardingConfirmationPage);
